import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Settings, Upload, Palette } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillSettings() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [form, setForm] = useState({ lab_name: '', lab_address: '', lab_phone: '', lab_email: '', lab_website: '', header_text: '', footer_text: '', primary_color: '#2563eb', terms_text: '' });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchLogo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/lis/settings/bill-logo/default`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        setLogoPreview(URL.createObjectURL(blob));
      } else {
        setLogoPreview(null);
      }
    } catch { setLogoPreview(null); }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/settings/bill-template`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setForm(f => ({ ...f, ...data }));
  }, [token]);
  useEffect(() => { fetchSettings(); fetchLogo(); }, [fetchSettings, fetchLogo]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/lis/settings/bill-template`, { method: 'PUT', headers, body: JSON.stringify(form) });
      if (res.ok) toast.success('Settings saved');
      else toast.error('Failed');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData(); fd.append('file', file); fd.append('lab_type', 'default');
    try {
      const res = await fetch(`${API}/api/lis/settings/bill-logo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (res.ok) { toast.success('Logo uploaded'); setLogoPreview(URL.createObjectURL(file)); }
      else toast.error('Upload failed');
    } finally { setUploadingLogo(false); }
  };

  return (
    <div data-testid="lis-bill-settings" className="max-w-2xl">
      <h1 className="text-xl font-bold text-slate-800 mb-4">Bill Template Settings</h1>
      <Card className="border-slate-200"><CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs text-slate-500 mb-1 block">Lab Name</label><Input value={form.lab_name} onChange={e => setForm(f => ({ ...f, lab_name: e.target.value }))} /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Phone</label><Input value={form.lab_phone} onChange={e => setForm(f => ({ ...f, lab_phone: e.target.value }))} /></div>
        </div>
        <div><label className="text-xs text-slate-500 mb-1 block">Address</label><Input value={form.lab_address} onChange={e => setForm(f => ({ ...f, lab_address: e.target.value }))} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs text-slate-500 mb-1 block">Email</label><Input value={form.lab_email} onChange={e => setForm(f => ({ ...f, lab_email: e.target.value }))} /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Website</label><Input value={form.lab_website} onChange={e => setForm(f => ({ ...f, lab_website: e.target.value }))} /></div>
        </div>
        <div><label className="text-xs text-slate-500 mb-1 block">Header Text</label><Input value={form.header_text} onChange={e => setForm(f => ({ ...f, header_text: e.target.value }))} placeholder="e.g. NABL Accredited" /></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Footer Text</label><Input value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="e.g. Thank you for choosing us" /></div>
        <div><label className="text-xs text-slate-500 mb-1 block">Terms & Conditions</label><textarea value={form.terms_text} onChange={e => setForm(f => ({ ...f, terms_text: e.target.value }))} className="w-full border rounded-md p-2 text-sm min-h-[80px] border-slate-200" /></div>
        <div className="flex items-center gap-4">
          <div><label className="text-xs text-slate-500 mb-1 block">Primary Color</label><div className="flex gap-2"><input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} className="w-10 h-10 rounded border" /><Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} className="w-28" /></div></div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Lab Logo</label>
            {logoPreview && (
              <img src={logoPreview} alt="Lab Logo" className="w-16 h-16 object-contain border border-slate-200 rounded mb-2 bg-white p-1" data-testid="lab-logo-preview" />
            )}
            <input type="file" accept="image/*" onChange={uploadLogo} className="text-sm" disabled={uploadingLogo} data-testid="lab-logo-upload-input" />
            {uploadingLogo && <p className="text-xs text-slate-400 mt-1">Uploading...</p>}
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="w-full">{saving ? 'Saving...' : 'Save Settings'}</Button>
      </CardContent></Card>
    </div>
  );
}
